export interface LoopingType {
  value: number;
  label: string;
  group: number;
  is_multilink: boolean;
  bbright_id: number;
}

/**
 * Task type sequences per group, used for the "next follow-up" looping logic.
 * Add new entries here when a new task type needs to participate in the sequence.
 */
const LOOPING_TYPES: Record<number, LoopingType[]> = {
  // Group 2 - Candidate
  2: [
    { value: 52, label: "Screening", group: 2, is_multilink: false, bbright_id: 85 },
    { value: 7, label: "Voorstelling", group: 2, is_multilink: true, bbright_id: 13 },
    { value: 10, label: "Selectiegesprek", group: 2, is_multilink: true, bbright_id: 16 },
    { value: 22, label: "Starter", group: 2, is_multilink: true, bbright_id: 28 },
    { value: 24, label: "Stopper", group: 2, is_multilink: true, bbright_id: 30 },
  ],
  // Group 1 - Company
  1: [
    { value: 6, label: "Voorstelling", group: 1, is_multilink: true, bbright_id: 12 },
    { value: 9, label: "Selectiegesprek", group: 1, is_multilink: true, bbright_id: 15 },
    { value: 21, label: "Starter", group: 1, is_multilink: true, bbright_id: 27 },
    { value: 23, label: "Stopper", group: 1, is_multilink: true, bbright_id: 29 },
  ],
  // Group 3 - Vacancy
  3: [
    { value: 8, label: "Voorstelling", group: 3, is_multilink: true, bbright_id: 14 },
    { value: 11, label: "Selectiegesprek", group: 3, is_multilink: true, bbright_id: 17 },
  ],
};

/**
 * Get the looping types array for a given group.
 */
export const getLoopingTypes = (group: number | string | undefined): LoopingType[] => {
  if (group === undefined || group === null) return [];
  const key = typeof group === "string" ? parseInt(group, 10) : group;
  return LOOPING_TYPES[key] ?? [];
};

/**
 * Get just the task type values for a given group (used for quick membership checks).
 */
export const getLoopingTypeValues = (group: number | string | undefined): number[] => {
  return getLoopingTypes(group).map((t) => t.value);
};

/**
 * Task type values that require a Completion Reason (Voorstelling, Selectiegesprek, Stopper across all groups).
 */
const COMPLETION_REASON_TYPES: number[] = [
  7, 6, 8,   // Voorstelling (group 2, 1, 3)
  10, 9, 11, // Selectiegesprek (group 2, 1, 3)
  24, 23,    // Stopper (group 2, 1)
  19, 20,    // Starter first week
];

export const isCompletionReasonType = (taskTypeValue: number | undefined | null): boolean => {
  if (taskTypeValue === undefined || taskTypeValue === null) return false;
  return COMPLETION_REASON_TYPES.includes(taskTypeValue);
};

/**
 * Stopper (group 1: 23, group 2: 24): the only task type whose Completion Reason is mandatory,
 * both on the task itself and on its "Add follow up".
 */
const STOPPER_TYPES: number[] = [23, 24];

export const isStopperType = (taskTypeValue: number | undefined | null): boolean => {
  if (taskTypeValue === undefined || taskTypeValue === null) return false;
  return STOPPER_TYPES.includes(taskTypeValue);
};

/**
 * A Stopper that is being created (not saved yet). Only these require a Completion Reason on
 * submit, so an existing Stopper in the group never blocks saving another task.
 */
export const isNewStopperTask = (
  task: { id?: number | string | null; taskType?: { value?: number } | null } | undefined | null,
): boolean => {
  if (!task) return false;
  return isStopperType(task.taskType?.value) && !task.id;
};

/**
 * Task type values for "Opstart 1e week" (group 1: 19, group 2: 20).
 * These are not part of the looping sequence, but should still preserve an
 * existing/prior `regarding` value when selected (like the multilink types).
 */
const STARTER_FIRST_WEEK_TYPES: number[] = [19, 20];

export const isStarterFirstWeekType = (taskTypeValue: number | undefined | null): boolean => {
  if (taskTypeValue === undefined || taskTypeValue === null) return false;
  return STARTER_FIRST_WEEK_TYPES.includes(taskTypeValue);
};

/**
 * Task type values for "Telefonisch contact" (group 1: 2, group 2: 3).
 * Group 3 (vacancy) has no Telefonisch contact type.
 */
const PHONE_CONTACT_TYPES: number[] = [2, 3];

/**
 * Voorstelling (6/7/8) and Selectiegesprek (9/10/11) across all groups.
 */
const VOORSTELLING_SELECTIEGESPREK_TYPES: number[] = [6, 7, 8, 9, 10, 11];

/**
 * Starter (21/22) and Stopper (23/24) across all groups. The `vacancy` field only applies
 * to these types.
 */
const STARTER_STOPPER_TYPES: number[] = [21, 22, 23, 24];

/**
 * True when the selected task type is a Starter/Stopper, i.e. the only types that use the
 * `vacancy` field. Every other type change should clear `vacancy`.
 */
export const isStarterStopperType = (taskTypeValue: number | undefined | null): boolean => {
  if (taskTypeValue === undefined || taskTypeValue === null) return false;
  return STARTER_STOPPER_TYPES.includes(taskTypeValue);
};

/**
 * True when changing a task's type must preserve the `regarding` already set on it.
 *
 * Switching a Voorstelling/Selectiegesprek or a Starter/Stopper to "Telefonisch contact"
 * keeps the entity the task was about; every other type change still clears it. Only
 * `regarding` carries over — `vacancy` stays Starter/Stopper-only.
 */
export const keepsRegardingOnTypeChange = (
  previousTaskTypeValue: number | undefined | null,
  nextTaskTypeValue: number | undefined | null,
): boolean => {
  if (previousTaskTypeValue === undefined || previousTaskTypeValue === null) return false;
  if (nextTaskTypeValue === undefined || nextTaskTypeValue === null) return false;
  return (
    PHONE_CONTACT_TYPES.includes(nextTaskTypeValue) &&
    (VOORSTELLING_SELECTIEGESPREK_TYPES.includes(previousTaskTypeValue) ||
      STARTER_STOPPER_TYPES.includes(previousTaskTypeValue))
  );
};
